<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="12" md="6" lg="4">
              <h2 class="text-h6 mb-4">{{ $t("systemSetting.label.title") }}</h2>
              <v-text-field
                v-model="receivingDateRangeDays"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :label="$t('systemSetting.label.receivingAvailableDateRangeDays')"
              />
              <v-text-field
                v-model="shippingDateRangeDays"
                class="mt-4"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :label="$t('systemSetting.label.shippingAvailableDateRangeDays')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn text @click="clear">{{ $t("common.action.clear") }}</v-btn>
              <v-btn
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                :loading="isSaving"
                @click="update"
              >
                {{ $t("userRegisterDialog.label.update") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row v-if="errorMessage">
            <v-col cols="12" md="8" lg="6">
              <v-alert type="error" variant="tonal" class="pre-wrap">{{ errorMessage }}</v-alert>
            </v-col>
          </v-row>

          <v-row v-if="successMessage">
            <v-col cols="12" md="8" lg="6">
              <v-alert type="success" variant="tonal">{{ successMessage }}</v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import api from "@/apis/staff";
import constant from "@/utils/constant";
import AppWrapperView from "@/components/views/AppWrapperView";

export default {
  components: { AppWrapperView },
  data: () => ({
    receivingDateRangeDays: "",
    shippingDateRangeDays: "",
    isSaving: false,
    errorMessage: "",
    successMessage: "",
  }),
  methods: {
    clear() {
      this.errorMessage = "";
      this.successMessage = "";
      // クリア時はサーバの現在設定値を再取得して表示を初期化する
      this.fetchCurrentValue();
    },
    async fetchCurrentValue() {
      // 片方失敗時も成功した設定値は必ず画面へ反映する
      const [receivingResult, shippingResult] = await Promise.allSettled([
        api.post("/config/receiving-date-range/get", {}),
        api.post("/config/shipping-date-range/get", {})
      ]);
      const errorMessages = [];
      const receivingLabel = this.$t("systemSetting.label.receivingAvailableDateRangeDays");
      const shippingLabel = this.$t("systemSetting.label.shippingAvailableDateRangeDays");

      if (receivingResult.status === "fulfilled") {
        if (receivingResult.value.data.status == constant.RESPONSE_STATUS.FAILURE) {
          errorMessages.push(`${receivingLabel}：${this.buildErrorMessage(receivingResult.value.data)}`);
        } else {
          this.receivingDateRangeDays = receivingResult.value.data.results?.receivingAvailableDateRangeDays ?? this.receivingDateRangeDays;
        }
      } else {
        errorMessages.push(`${receivingLabel}：${this.$t("common.message.errorAndRetry")}`);
      }

      if (shippingResult.status === "fulfilled") {
        if (shippingResult.value.data.status == constant.RESPONSE_STATUS.FAILURE) {
          errorMessages.push(`${shippingLabel}：${this.buildErrorMessage(shippingResult.value.data)}`);
        } else {
          this.shippingDateRangeDays = shippingResult.value.data.results?.shippingAvailableDateRangeDays ?? this.shippingDateRangeDays;
        }
      } else {
        errorMessages.push(`${shippingLabel}：${this.$t("common.message.errorAndRetry")}`);
      }

      this.errorMessage = errorMessages.join("\n");
    },
    async update() {
      this.errorMessage = "";
      this.successMessage = "";

      const receivingValidationMessage = this.validateRangeValue(
        this.receivingDateRangeDays,
        this.$t("systemSetting.label.receivingAvailableDateRangeDays")
      );
      if (receivingValidationMessage) {
        this.errorMessage = receivingValidationMessage;
        return;
      }

      const shippingValidationMessage = this.validateRangeValue(
        this.shippingDateRangeDays,
        this.$t("systemSetting.label.shippingAvailableDateRangeDays")
      );
      if (shippingValidationMessage) {
        this.errorMessage = shippingValidationMessage;
        return;
      }

      this.isSaving = true;
      // 逐次更新し、部分成功時は更新済み項目を明示する
      try {
        const receivingLabel = this.$t("systemSetting.label.receivingAvailableDateRangeDays");
        const shippingLabel = this.$t("systemSetting.label.shippingAvailableDateRangeDays");
        const updatedLabels = [];
        const errorMessages = [];

        const receivingResult = await this.updateRangeSetting(
          "/config/receiving-date-range/update",
          { receivingAvailableDateRangeDays: this.receivingDateRangeDays },
          "receivingAvailableDateRangeDays",
          receivingLabel
        );
        if (receivingResult.updated) {
          this.receivingDateRangeDays = receivingResult.value ?? this.receivingDateRangeDays;
          updatedLabels.push(receivingLabel);
        } else if (receivingResult.errorMessage) {
          errorMessages.push(receivingResult.errorMessage);
        }

        const shippingResult = await this.updateRangeSetting(
          "/config/shipping-date-range/update",
          { shippingAvailableDateRangeDays: this.shippingDateRangeDays },
          "shippingAvailableDateRangeDays",
          shippingLabel
        );
        if (shippingResult.updated) {
          this.shippingDateRangeDays = shippingResult.value ?? this.shippingDateRangeDays;
          updatedLabels.push(shippingLabel);
        } else if (shippingResult.errorMessage) {
          errorMessages.push(shippingResult.errorMessage);
        }

        if (errorMessages.length) {
          if (updatedLabels.length) {
            this.errorMessage = `一部の設定のみ更新されました（更新済み: ${updatedLabels.join(" / ")}）\n${errorMessages.join("\n")}`;
          } else {
            this.errorMessage = errorMessages.join("\n");
          }
        } else {
          this.successMessage = this.$t("systemSetting.message.saved");
        }
      } finally {
        this.isSaving = false;
      }
    },
    async updateRangeSetting(path, payload, resultFieldName, label) {
      try {
        const response = await api.post(path, payload);
        if (response.data.status == constant.RESPONSE_STATUS.FAILURE) {
          return {
            updated: false,
            value: "",
            errorMessage: `${label}：${this.buildErrorMessage(response.data)}`
          };
        }
        return {
          updated: true,
          value: response.data.results?.[resultFieldName] ?? "",
          errorMessage: ""
        };
      } catch {
        return {
          updated: false,
          value: "",
          errorMessage: `${label}：${this.$t("common.message.errorAndRetry")}`
        };
      }
    },
    validateRangeValue(value, label) {
      // API送信前に 0-365 の整数のみ許可する
      if (!/^\d+$/.test(value)) {
        return this.$t("common.message.error") + `：${label}は0〜365の整数を入力してください`;
      }
      const num = Number(value);
      if (num < 0 || num > 365) {
        return this.$t("common.message.error") + `：${label}は0〜365の範囲で入力してください`;
      }
      return "";
    },
    buildErrorMessage(response) {
      // エラー詳細が複数ある場合は改行で連結して表示する
      if (response.errorList?.length) {
        return response.errorList.map(e => e.message).join("\n");
      }
      return response.errorMessage || this.$t("common.message.error");
    }
  },
  mounted() {
    this.fetchCurrentValue();
  }
};
</script>

<style scoped>
.pre-wrap {
  white-space: pre-line;
}
</style>
